import { useMemo } from 'react';
import { ProcessorCapability, ProcessorCapabilityExtras } from 'lib/graphql';
import {
  CutsheetWizardFormValues,
  ExtraOption,
  ExtraSelection,
} from '../types';

export function useExtrasOptions(
  activeCapability: ProcessorCapability | undefined,
  values: CutsheetWizardFormValues,
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean,
  ) => void,
) {
  /**
   * Extract extras with specification-first priority.
   *
   * Processors can configure extras at two levels:
   * 1. Base capability level (cap.extras) - applies to all cutsheets
   * 2. Specification level (cap.activeSpecification.extras) - overrides for specific configurations
   *
   * Priority order:
   * 1. Check activeSpecification.extras FIRST (specification-specific overrides)
   * 2. Fall back to cap.extras (base capability defaults)
   *
   * This ensures processors can customize extras/pricing for different specifications
   * (e.g., different extras for "premium" vs "standard" cutsheet configurations).
   */
  const extrasRaw = useMemo<ProcessorCapabilityExtras[]>(() => {
    const cap = activeCapability;
    const candidates = [
      // Priority 1: Specification-specific extras (overrides)
      (cap?.activeSpecification as { extras?: ProcessorCapabilityExtras[] })
        ?.extras,
      // Priority 2: Base capability extras (defaults)
      cap?.extras,
    ];
    for (const cand of candidates) {
      if (Array.isArray(cand) && cand.length)
        return cand.filter((e): e is ProcessorCapabilityExtras => !!e);
    }
    return [];
  }, [activeCapability]);

  const extrasOptions = useMemo(
    () =>
      extrasRaw
        .filter((e) => e && (e.isActive ?? true))
        .map(
          (e): ExtraOption => ({
            value: String(e?.name ?? e?._id ?? ''),
            label: String(e?.name ?? ''),
            description: e?.description ?? '',
            fee: e?.fee ?? 0,
            isActive: e?.isActive ?? false,
            minLbs: e?.minLbs ?? 0,
            pricePerPound: e?.pricePerPound ?? 0,
            type: e?.type ?? '',
          }),
        )
        .filter((o) => !!o.value && !!o.label),
    [extrasRaw],
  );

  const onToggleExtra = (value: string, checked: boolean) => {
    const currentExtras = values.extras || [];
    const existingExtra = extrasRaw.find(
      (e) => String(e?.name ?? e?._id ?? '') === value,
    );

    if (!existingExtra) return;

    if (checked) {
      // Add the extra with all its properties including _id for mutation matching
      const newExtra: ExtraSelection = {
        _id: existingExtra._id,
        name: String(existingExtra.name ?? ''),
        type: String(existingExtra.type ?? ''),
        pricePerPound: existingExtra.pricePerPound ?? undefined,
        fee: existingExtra.fee ?? undefined,
        minLbs: existingExtra.minLbs ?? undefined,
        isActive: true,
        quantity: existingExtra.minLbs || 0,
      };
      setFieldValue('extras', [...currentExtras, newExtra], true);
    } else {
      // Remove the extra
      const filteredExtras = currentExtras.filter(
        (extra) => extra.name !== existingExtra.name,
      );
      setFieldValue('extras', filteredExtras, true);
    }
  };

  const onQuantityChange = (value: string, quantity: number) => {
    const currentExtras = values.extras || [];
    const existingExtra = extrasRaw.find(
      (e) => String(e?.name ?? e?._id ?? '') === value,
    );

    if (!existingExtra) return;

    const updatedExtras = currentExtras.map((extra) =>
      extra.name === existingExtra.name ? { ...extra, quantity } : extra,
    );
    setFieldValue('extras', updatedExtras, true);
  };

  return {
    extrasOptions,
    extrasRaw,
    onToggleExtra,
    onQuantityChange,
  };
}
